import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Snackbar,
    Typography,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Client } from '../../types/client';
import { clientService } from '../../services/clientService';
import ClientDialog from './ClientDialog';
import ConfirmDialog from '../common/ConfirmDialog';

const ClientList: React.FC = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    
    const [clients, setClients] = useState<Client[]>([]);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
    const apiError = (error: any, fallback: string) => error?.response?.data?.error || fallback;

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        try {
            setLoading(true);
            const data = await clientService.getClients();
            setClients(data);
        } catch (error) {
            console.error('Erro ao carregar clientes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleNewClient = () => {
        setSelectedClient(null);
        setIsDialogOpen(true);
    };

    const handleEditClient = (client: Client) => {
        setSelectedClient(client);
        setIsDialogOpen(true);
    };

    const handleDeleteClient = (client: Client) => {
        setSelectedClient(client);
        setIsDeleteDialogOpen(true);
    };

    const handleSaveClient = async (clientData: Client) => {
        try {
            if (clientData.id) {
                await clientService.updateClient(clientData.id, clientData);
            } else {
                await clientService.createClient(clientData);
            }
            loadClients();
            setIsDialogOpen(false);
            setFeedback({ severity: 'success', message: 'Cliente salvo.' });
        } catch (error) {
            // Diálogo continua aberto para o usuário corrigir (ex.: nome duplicado)
            setFeedback({ severity: 'error', message: apiError(error, 'Erro ao salvar cliente.') });
        }
    };

    const handleConfirmDelete = async () => {
        if (selectedClient) {
            try {
                await clientService.deleteClient(selectedClient.id);
                loadClients();
                setFeedback({ severity: 'success', message: 'Cliente excluído.' });
            } catch (error) {
                setFeedback({ severity: 'error', message: apiError(error, 'Erro ao excluir cliente.') });
            } finally {
                setIsDeleteDialogOpen(false);
            }
        }
    };

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    {t('manager.clients.title')}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleNewClient}
                >
                    {t('manager.clients.new')}
                </Button>
            </Box>

            <Grid container spacing={3}>
                {clients.map((client) => (
                    <Grid item xs={12} sm={6} md={4} key={client.id}>
                        <Card>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                    <Typography variant="h6" component="div">
                                        {client.name}
                                    </Typography>
                                    <Box>
                                        <IconButton
                                            size="small"
                                            aria-label={`Editar ${client.name}`}
                                            onClick={() => handleEditClient(client)}
                                            sx={{ mr: 1 }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            aria-label={`Excluir ${client.name}`}
                                            onClick={() => handleDeleteClient(client)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                                {client.description && (
                                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                                        {client.description}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <ClientDialog
                open={isDialogOpen}
                client={selectedClient}
                onClose={() => setIsDialogOpen(false)}
                onSave={handleSaveClient}
            />

            <ConfirmDialog
                open={isDeleteDialogOpen}
                title={t('manager.clients.deleteConfirmation.title')}
                message={t('manager.clients.deleteConfirmation.message', { name: selectedClient?.name })}
                onConfirm={handleConfirmDelete}
                onClose={() => setIsDeleteDialogOpen(false)}
            />

            <Snackbar
                open={!!feedback}
                autoHideDuration={6000}
                onClose={() => setFeedback(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                {feedback ? (
                    <Alert severity={feedback.severity} variant="filled" onClose={() => setFeedback(null)}>
                        {feedback.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Box>
    );
};

export default ClientList; 